import { Link } from "react-router-dom";
import { useAuth } from "../../../context/authProvider"
import SettingsIcon from "../../../img/svg/settingsIcon";
import MyList from "./myList";
import MyPurchases from "./myPurchases";
import MyBuyerScore from "./myBuyerScore";
import { useState } from "react";
import LoadingBalls from "../../core/loadingBalls";
import PhoneVerifier from "./phoneVerifier";
import CheckIcon from "../../../img/svg/checkIcon";
import EmailVerifier from "./emailVerifier";
import { maskPhoneNumber, onlyDigits, validatePhoneNumber } from "../../../utils/functions";

export default function MyArea() {

    const { user, logOut, updateUserPhone } = useAuth();

    const [phone, setPhone] = useState(user?.phoneNumber || "");
    const [editPhone, setEditPhone] = useState(false);
    const [phoneFeedback, setPhoneFeedback] = useState("");
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState(0);

    const handleCancel = () => {
        setPhone(user?.phoneNumber || "");
        setPhoneFeedback("");
        setEditPhone(false);
    }

    const setHandledPhone = (value: string) => {
        const p = handlePhone(value)
        setPhone(p);
    }

    // máscara (xx) xxxxx-xxxx compartilhada com o cadastro (utils/functions.ts)
    const handlePhone = (value: string) => maskPhoneNumber(value);

    const doEditPhone = async () => {
        const parsedPhone = onlyDigits(phone);
        if (parsedPhone === user?.phoneNumber) {
            return null;
        }
        if (!validatePhoneNumber(parsedPhone)) {
            setPhoneFeedback("Telefone inválido. Informe DDD + celular com 11 dígitos.");
            return null;
        }
        setLoading(true);
        setPhoneFeedback("");
        const update = await updateUserPhone(parsedPhone);
        if (!update) {
            // updateUserPhone recusa a alteração quando o telefone já está verificado
            // (pelo próprio usuário via SMS ou por um admin no painel)
            setPhoneFeedback("Não foi possível alterar o telefone. Telefones já verificados não podem ser alterados.");
            setLoading(false);
            return null;
        }
        setEditPhone(false);
        setLoading(false);
    }

    return (
        <main className="max-w-7xl mx-auto px-4 md:px-10 py-24 mb-16 animate-superfade">
            {/* Profile Header */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-16 gap-8">
                <div className="flex flex-col md:flex-row items-center gap-6">
                    <div className="w-24 h-24 bg-moninho rounded-full flex items-center justify-center text-3xl font-black text-primary border-4 border-white shadow-xl">
                        {user?.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="text-center md:text-left">
                        <div className="flex flex-col md:flex-row items-center gap-3 mb-2">
                            <h1 className="text-3xl font-black text-primary tracking-tight">
                                {user?.name}
                            </h1>
                            <div className="flex gap-2">
                                {user?.roles?.includes("admin") && (
                                    <span className="bg-primary text-secondary text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full">Admin</span>
                                )}
                                {user?.roles?.includes("advertiser") && (
                                    <span className="bg-secondary text-white text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full">Anunciante</span>
                                )}
                            </div>
                        </div>
                        <p className="text-mono text-sm tracking-wide">Bem-vindo à sua área exclusiva GMT-3</p>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    {
                        user?.roles.includes("admin") || user?.roles.includes("advertiser") ?
                            <Link
                                to="/admin"
                                className="flex gap-2 items-center bg-white border-2 border-primary/10 px-6 py-3 rounded-2xl hover:border-secondary hover:text-secondary transition-all active:scale-95 group shadow-sm"
                            >
                                <SettingsIcon fill="currentColor" />
                                <span className="text-xs font-black tracking-widest uppercase">Administração</span>
                            </Link>
                            : null
                    }
                    <button
                        onClick={logOut}
                        className="px-6 py-3 bg-red-50 text-red-600 font-black tracking-widest text-xs rounded-2xl hover:bg-red-600 hover:text-white transition-all active:scale-95 shadow-sm"
                    >
                        SAIR
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">
                {/* Navigation Sidebar */}
                <aside className="lg:col-span-1">
                    <nav className="flex flex-row lg:flex-col gap-2 p-2 bg-moninho/50 backdrop-blur-md rounded-[2rem] border border-white/50 sticky top-24 overflow-x-auto no-scrollbar">
                        {[
                            { label: "Informações", id: 0 },
                            { label: "Minha Lista", id: 1 },
                            { label: "Minhas Compras", id: 2 },
                            { label: "Meu Score", id: 3 }
                        ].map((item) => (
                            <button
                                key={item.id}
                                onClick={() => setTab(item.id)}
                                className={`flex-1 lg:flex-none text-left px-6 py-4 rounded-xl text-xs font-black tracking-widest transition-all ${tab === item.id
                                    ? "bg-white text-primary shadow-lg shadow-primary/5"
                                    : "text-mono hover:bg-white/50 hover:text-primary"
                                    }`}
                            >
                                {item.label.toUpperCase()}
                            </button>
                        ))}
                    </nav>
                </aside>

                {/* Content Area */}
                <div className="lg:col-span-3">
                    {
                        tab === 0 ?
                            <div className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-xl shadow-primary/5 border border-primary/5 space-y-12 animate-superfade">
                                <div className="space-y-2">
                                    <h2 className="text-2xl font-black text-primary tracking-tight">Informações da Conta</h2>
                                    <div className="w-12 h-1 bg-secondary" />
                                </div>

                                <div className="space-y-8 divide-y divide-surface">
                                    {/* Email Row */}
                                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 pt-0 first:pt-0">
                                        <div className="space-y-1">
                                            <p className="text-[10px] font-black text-mono tracking-[0.2em] uppercase">Email de Acesso</p>
                                            <p className="text-lg font-bold text-primary">{user?.email}</p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {
                                                !user?.emailVerified ?
                                                    <EmailVerifier />
                                                    :
                                                    <div className="flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-full border border-green-100 shadow-sm">
                                                        <CheckIcon fill="#15803d" />
                                                        <span className="text-[10px] font-black tracking-widest uppercase">Verificado</span>
                                                    </div>
                                            }
                                        </div>
                                    </div>

                                    {/* Phone Row */}
                                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center pt-8">
                                        <div className="space-y-1 flex-1">
                                            <p className="text-[10px] font-black text-mono tracking-[0.2em] uppercase">Telefone de Contato</p>
                                            <input
                                                disabled={!editPhone}
                                                className="bg-moninho border border-transparent disabled:opacity-80 focus:border-secondary focus:bg-white px-4 py-3 rounded-xl text-lg font-bold transition-all outline-none text-primary w-full max-w-[240px]"
                                                type="text"
                                                value={handlePhone(phone)}
                                                onChange={e => setHandledPhone(e.target.value)}
                                                placeholder="Não definido"
                                            />
                                            {
                                                phoneFeedback ?
                                                    <p className="text-red-600 text-xs font-bold max-w-[320px]">{phoneFeedback}</p>
                                                    : null
                                            }

                                        </div>
                                        <div className="flex items-center gap-3">

                                            {
                                                user?.phoneVerified && !editPhone && (
                                                    <div className="flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-full border border-green-100 shadow-sm">
                                                        <CheckIcon fill="#15803d" />
                                                        <span className="text-[10px] font-black tracking-widest uppercase">Verificado</span>
                                                    </div>
                                                )
                                            }
                                        </div>

                                        <div className="flex items-center gap-3 shrink-0">
                                            {
                                                loading ?
                                                    <div className="px-8"><LoadingBalls width={60} /></div>
                                                    :
                                                    editPhone ?
                                                        <div className="flex gap-2">
                                                            <button onClick={handleCancel} className="px-6 py-3 bg-white border border-surface text-mono font-bold tracking-widest text-[10px] rounded-xl hover:border-primary hover:text-primary transition-all active:scale-95">CANCELAR</button>
                                                            <button
                                                                disabled={!validatePhoneNumber(phone)}
                                                                onClick={doEditPhone}
                                                                className="px-6 py-3 bg-secondary text-white font-black tracking-widest text-[10px] rounded-xl hover:opacity-90 transition-all active:scale-95 disabled:bg-surface disabled:text-mono"
                                                            >
                                                                SALVAR
                                                            </button>
                                                        </div>
                                                        :
                                                        !user?.phoneVerified && (
                                                            <button
                                                                onClick={() => setEditPhone(true)}
                                                                className="px-6 py-3 border-2 border-primary text-primary font-black tracking-widest text-[10px] rounded-xl hover:bg-primary hover:text-white transition-all active:scale-95"
                                                            >
                                                                {user?.phoneNumber ? "ALTERAR" : "ADICIONAR"}
                                                            </button>
                                                        )
                                            }
                                        </div>
                                    </div>

                                    {/* Verification Message */}
                                    {
                                        user?.roles.includes("advertiser") && user?.phoneNumber && !user.phoneVerified && !editPhone && (
                                            <div className="pt-8 w-full">
                                                <div className="bg-red-50 border border-red-100 rounded-3xl p-6 space-y-4">
                                                    <p className="text-center text-red-700 font-bold tracking-wide">Seu telefone ainda não foi verificado para anúncios.</p>
                                                    <div className="max-w-xs mx-auto">
                                                        <PhoneVerifier disabled={editPhone || !user?.phoneNumber} phoneN={phone} />
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    }
                                </div>
                            </div>
                            : tab === 1 ?
                                <MyList />
                                : tab === 2 ?
                                    <MyPurchases />
                                    : tab === 3 ?
                                        <MyBuyerScore />
                                        : null
                    }
                </div>
            </div>
        </main>
    )
}
